import { useCallback } from 'react';

import clamp from 'lodash/clamp';

import { AnalyticsStep } from '@atlaskit/adf-schema/steps/analytics';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	INPUT_METHOD,
	MODE,
} from '@atlaskit/editor-common/analytics';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
// eslint-disable-next-line import/order
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';

import type { AIPlugin } from '../../../aiPluginType';
import type { EditorAIAnalyticEventPayload, FailedSource } from '../../../types/analytics';
// eslint-disable-next-line import/order
import type { BackendModel } from '../../../types/prompt-request-types';
// eslint-disable-next-line import/order
import type { EditorPluginAIProvider, FeedbackMetadata, AIGlobalOptIn } from '../../../types/types';
// eslint-disable-next-line import/order
import type { SelectionType } from '../../prebuilt/config-items/config-items';
import { getPackageInfo } from '../version';
// eslint-disable-next-line import/order
import type {
	FailedStep,
	InvokedFrom,
	InvokedFromTriggerMethod,
	ServerErrorStep,
	StepName,
	UserFlowStepAttributes,
} from './analyticsFlowTypes';
import type { SELECTION_CATEGORY, NodeAndMarkCounts } from './enums';

const WHITESPACE_REGEX = /\s+/u;

export function addAIPluginCommonInfoToPayload(payload: EditorAIAnalyticEventPayload):
	| {
			action: ACTION.INITIATED;
			actionSubject: ACTION_SUBJECT.AI_INTERACTION;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.INITIATED;
			actionSubject: ACTION_SUBJECT.AI_INTERACTION;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.VIEWED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.VIEWED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.ERROR;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.ERROR;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.DISMISSED;
			actionSubject: ACTION_SUBJECT.AI_INTERACTION;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.DISMISSED;
			actionSubject: ACTION_SUBJECT.AI_INTERACTION;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.ACTIONED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.ACTIONED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.SUBMITTED;
			actionSubject: ACTION_SUBJECT.AI_FEEDBACK;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.SUBMITTED;
			actionSubject: ACTION_SUBJECT.AI_FEEDBACK;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: ACTION.DISCARDED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.OPERATIONAL;
			objectId?: undefined;
	  }
	| {
			action: ACTION.DISCARDED;
			actionSubject: ACTION_SUBJECT.AI_RESULT;
			actionSubjectId?: ACTION_SUBJECT_ID.EDITOR_PLUGIN_AI | undefined;
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			childObjectId?: undefined;
			containerId?: undefined;
			eventType: EVENT_TYPE.TRACK;
			nonPrivacySafeAttributes?: undefined;
			objectId?: undefined;
	  }
	| {
			action: 'failed';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'markdownSerializer';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'fallback';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'markdownSerializer';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'nonErrorFallback';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'markdownSerializer';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'failed';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'markdownParser';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'fallback';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'proseMirrorReplace';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'fallback';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'replaceWithEmptyAIContent';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'responseFeedbackButton';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'copyButton';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'refinementButton';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'refinementOption';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'editPromptButton';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'click';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'retryPromptButton';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: StepName;
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'userFlow';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'init';
			actionSubject: 'editor';
			actionSubjectId: 'editorPluginAI';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.TRACK;
	  }
	| {
			action: 'unhandledErrorCaught';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'decorationPluginFactory';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'triggerOptInFlow';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'aiGlobalOptIn';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.UI;
	  }
	| {
			action: 'failed';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'experienceApplication';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'error';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'eventHubPlugin';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'collected';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'nudgeMetrics';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'updated';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'aiImmediateReaction';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.TRACK;
	  }
	| {
			action: 'error';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'adfStreaming';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'info';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'adfStreaming';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'error';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'adfStreamingEndSession';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.OPERATIONAL;
	  }
	| {
			action: 'captured';
			actionSubject: 'editorPluginAI';
			actionSubjectId: 'toolCall';
			attributes:
				| {
						adfPrompt?: boolean;
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiGroupId?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						autoAcceptAIChanges?: boolean;
						backendModel?: BackendModel;
						channelId?: string;
						constructor: Function;
						contentCopiedSometime?: boolean;
						duration?: number;
						experienceName?: string;
						featureId?: string;
						has3pSources?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						hasOwnProperty(v: PropertyKey): boolean;
						includeKnowledgeFromCurrentPage?: boolean;
						inlineStreamingAvailable?: boolean;
						invokedFor?: string;
						invokedFrom?: InvokedFrom;
						isInvokedFromEmptyPage?: boolean;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						isPrototypeOf(v: Object): boolean;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						promptVersion?: string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						propertyIsEnumerable(v: PropertyKey): boolean;
						sawAupViolation?: boolean;
						selectionCategory?: SELECTION_CATEGORY;
						selectionCharCount?: number;
						selectionNodes?: NodeAndMarkCounts;
						selectionWordCount?: number;
						source?: StepName;
						sourceProduct?: string | null;
						stepOccurrence?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | string;
						target?: StepName;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toLocaleString(): string;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						toString(): string;
						totalDuration?: number;
						traceIds?: string[];
						triggerMethod?: InvokedFromTriggerMethod;
						// eslint-disable-next-line @typescript-eslint/method-signature-style
						valueOf(): Object;
				  }
				| {
						agentCreatorType?: string;
						agentExternalConfigReference?: string;
						agentId?: string;
						agentIsDefault?: boolean;
						agentName?: string;
						aiExperienceName?: string;
						aiFeatureName: string;
						aiInteractionID: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						has3pSources?: boolean;
						invokedFrom?: string;
						isAIFeature: 1;
						isStreamingEnded?: boolean;
						nudgeMetrics?: Record<string, number | undefined>;
						proactiveAIGenerated: 0 | 1;
						promptVersion?: string;
						singleInstrumentationID: string;
						sourceProduct?: string | null;
						traceIds?: string[];
						userGeneratedAI: 0 | 1;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						appearance?: EditorAppearance;
						configItemTitle: string;
						inputOutputDiffRatio?: string;
						lastTriggeredFrom?: string;
						product: EditorPluginAIProvider['product'];
						productSuccessStatus?: 'submitted' | 'failed' | 'discarded' | 'unknown';
						responseTooSimilar?: boolean;
						retryPromptCount?: number;
						sentiment: FeedbackMetadata['sentiment'];
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						experienceType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownSerializer';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						fallbackState: {
							[nodeTypeName: string]: {
								[errorMessage: string]: number;
							};
						};
						source?: 'summarize' | 'generate' | 'shorten' | 'enhance';
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						nonErrorFallbackState: {
							[nodeTypeName: string]: number;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType?: 'markdownParser';
						source?: FailedSource;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						isReplacementEmpty?: boolean;
						isSelectionEmpty: boolean;
						target: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						isBlock: boolean;
						isSelectionEmpty: boolean;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						componentStack: string;
						errorMessage?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'cannotEnqueueMoreThanOneEvent';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'noEventExistsToBeFired';
						errorType: 'userFlowAnalyticsError';
						target?: StepName;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorSubType: 'userFlowAlreadyFinished';
						errorType: 'userFlowAnalyticsError';
						queuedStepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						stepArgs?: {
							attributes?: Partial<UserFlowStepAttributes>;
							stepName: StepName;
						};
						target?: StepName;
				  }
				| {
						aiGlobalOptIn: AIGlobalOptIn['status'] | undefined;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorMessage?: string;
						errorType: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'replaceStepNotFound';
				  }
				| {
						adjustSelection?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						errorType: 'promptConfigItemNotFound';
						prompt?: string;
						promptText?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						nudgeMetrics: Record<string, number | undefined>;
				  }
				| {
						aiGroupId: string | undefined;
						aiOutputChars: number;
						aiOutputWords: number;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId: string;
						experienceName: string | undefined;
						nextActionLatencyMs: number;
						nextActionRemovedAiChars: number | undefined;
						nextActionRemovedAiWords: number | undefined;
						nextActionTouchedAiRanges: number | undefined;
						nextActionType: 'undo' | 'redo' | 'regen' | 'delete' | 'other';
						promptVersion?: string;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						errorData?: object;
						errorMessage: string;
						experienceName: string | null;
						featureId?: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiInteractionID?: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						aiSessionId?: string;
						contentStatistics?: ContentStatistics;
						data?: object;
						experienceName: string | null;
						featureId?: string;
						message: string;
						retryAttemptNumber?: number;
						selectionCategory: SELECTION_CATEGORY | null;
						selectionType: SelectionType | null;
						source:
							| {
									isCreateAction: boolean;
									type: 'orchestrator';
							  }
							| {
									type: 'plugin';
							  };
						streamId: string | null;
						traceId?: string;
				  }
				| {
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						endSessionCallCount: number;
						isDoubleCall: boolean;
						lastEndedSessionId: string | null;
						sessionStarted: boolean;
				  }
				| {
						actionId: string;
						aiPluginPackageName: string;
						aiPluginPackageVersion: string;
						commandType:
							| (
									| 'wrap'
									| 'moveNode'
									| 'replaceNode'
									| 'insertNodeBefore'
									| 'insertNodeAfter'
									| 'appendNodeToEnd'
									| 'removeNode'
									| 'removeNodes'
									| 'setAttrs'
									| 'wrapNode'
									| 'appendNode'
							  )
							| 'invalid';
						traceId?: string;
				  };
			eventType: EVENT_TYPE.TRACK;
	  } {
	const packageInfo = getPackageInfo();
	const attributes = payload.attributes || {};

	return {
		...payload,
		attributes: {
			...attributes,
			aiPluginPackageName: packageInfo.packageName,
			aiPluginPackageVersion: packageInfo.packageVersion,
		},
	};
}

// This utils is referenced from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
// Note: this adds extra step to a transaction which effectively marks it as docChanged and can cause editor onchange to fire on dispatch
function attachPayloadIntoTransaction({
	editorState,
	payload,
	channel = FabricChannel.editor,
}: {
	channel?: string;
	editorState: EditorState;
	payload: EditorAIAnalyticEventPayload;
}) {
	return (tr: Transaction) => {
		const { storedMarks } = tr;
		const pos = tr.mapping.map(editorState.selection.$from.pos, -1);
		tr.step(
			new AnalyticsStep(
				[
					{
						payload: addAIPluginCommonInfoToPayload(payload),
						channel,
					},
				],
				[],
				// We don't understand now why we need position in AnalyticsStep.
				// So we are keeping same as it is in original util.
				// Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
				// We need to create the step based on a position, this prevent split history for relative changes.
				pos,
			),
		);

		// We don't understand now why we need to store marks again in transaction.
		// So we are keeping same as it is in original util.
		// Below comment is taken from original util from packages/editor/editor-core/src/analytics-api/attach-payload-into-transaction.ts
		// When you add a new step all the storedMarks are removed it
		if (storedMarks) {
			tr.setStoredMarks(storedMarks);
		}

		return true;
	};
}

/**
 *  This will add extra step to the transaction with analytics info
 *  this information will be be picked up by the analytics plugin which will fire the event
 *  please note that this will cause the transaction to be marked as docChanged because of adding extra transaction step
 *  avoid creating and dispatching adhoc transaction, but rather reuse existing transactions that are already dispatched
 *
 * The `addToHistory` parameter controls whether this transaction should be recorded in the
 * editor's history. By default, `addToHistory` is set to `true`, meaning the transaction
 * will be added to the history stack, allowing for undo/redo operations. However, if
 * `addToHistory` is set to `false`, the transaction will not be recorded in the history,
 * which can be useful for operations that should not be undoable, such as logging or
 * non-destructive analytics events.
 */
export function addAnalytics({
	editorState,
	tr,
	payload,
	channel = FabricChannel.editor,
	addToHistory = true,
}: {
	addToHistory?: boolean;
	channel?: string;
	editorState: EditorState;
	payload: EditorAIAnalyticEventPayload;
	tr: Transaction;
}): Transaction {
	attachPayloadIntoTransaction({ editorState, payload, channel })(tr);
	if (!addToHistory) {
		tr.setMeta('addToHistory', false);
	}
	return tr;
}

// File copied from packages/editor/editor-core/src/types/command.ts
export type AnalyticsEventPayloadCallback = (
	state: EditorState,
) => EditorAIAnalyticEventPayload | undefined;
export type CommandDispatch = (tr: Transaction) => void;
export type Command = (
	state: EditorState,
	dispatch?: CommandDispatch,
	view?: EditorView,
) => boolean;
export type HigherOrderCommand = (command: Command) => Command;

// Below function is copied from packages/editor/editor-core/src/plugins/analytics/utils.ts
export function withAnalytics({
	payload,
	channel = FabricChannel.editor,
}: {
	channel?: string;
	payload: EditorAIAnalyticEventPayload | AnalyticsEventPayloadCallback;
}): HigherOrderCommand {
	return (command) => (state, dispatch, view) =>
		command(
			state,
			(tr) => {
				if (dispatch) {
					if (payload instanceof Function) {
						const dynamicPayload = payload(state);
						if (dynamicPayload) {
							dispatch(
								addAnalytics({
									editorState: state,
									tr,
									payload: dynamicPayload,
									channel,
								}),
							);
						}
					} else {
						dispatch(addAnalytics({ editorState: state, tr, payload, channel }));
					}
				}
			},
			view,
		);
}
export type FireAIAnalyticsEvent = {
	channel?: string;
	payload: EditorAIAnalyticEventPayload;
};

export type FireAIAnalyticsEventCallback = (args: FireAIAnalyticsEvent) => void;

export function createFireAIAnalyticsEvent(
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): FireAIAnalyticsEventCallback {
	return ({ payload, channel = FabricChannel.editor }: FireAIAnalyticsEvent) => {
		if (!createAnalyticsEvent) {
			return;
		}

		const fireEvent = () =>
			createAnalyticsEvent(addAIPluginCommonInfoToPayload(payload))?.fire(channel);
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((window as any).requestIdleCallback) {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).requestIdleCallback(fireEvent);
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} else if ((window as any).requestAnimationFrame) {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(window as any).requestAnimationFrame(fireEvent);
		} else {
			setTimeout(fireEvent, 0);
		}
	};
}

/*
 * gets helper function to fire analytics for ai plugin
 */
export function useFireAIAnalyticsEvent(): FireAIAnalyticsEventCallback {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAIAnalyticsEvent: FireAIAnalyticsEventCallback = useCallback(
		(args) => createFireAIAnalyticsEvent(createAnalyticsEvent)(args),
		[createAnalyticsEvent],
	);
	return fireAIAnalyticsEvent;
}

/**
 * Gets the single instrumentation ID from the given channel ID and last AI interaction ID.
 */
export function getSingleInstrumentationID(
	channelId?: string,
	lastAiInteractionID?: string,
): string {
	return channelId ?? lastAiInteractionID ?? '';
}

/**
 * Checks if the given step is an error step.
 */
export function isErrorStepAttributes(
	step: Partial<UserFlowStepAttributes> | undefined,
): step is Partial<ServerErrorStep['attributes'] | FailedStep['attributes']> {
	if (!step) {
		return false;
	}

	return 'errorKey' in step || 'errorType' in step || 'statusCode' in step;
}

/**
 * Emits a UI "button viewed" event when an AI button becomes visible
 * in the editor’s floating toolbar.
 */
export const fireButtonViewedAnalyticsEvent = ({
	api,
	actionSubjectId,
	attributes,
}: {
	actionSubjectId:
		| ACTION_SUBJECT_ID.AI_ASK_ROVO_BUTTON
		| ACTION_SUBJECT_ID.AI_HERO_PROMPT_BUTTON
		| ACTION_SUBJECT_ID.AI_DEFINITIONS_DEFINE_BUTTON;
	api?: ExtractInjectionAPI<AIPlugin>;
	attributes: { selectionCharCount: number; selectionWordCount: number };
}): void => {
	api?.analytics?.actions.fireAnalyticsEvent({
		action: ACTION.VIEWED,
		actionSubject: ACTION_SUBJECT.BUTTON,
		actionSubjectId,
		eventType: EVENT_TYPE.UI,
		attributes: {
			inputMethod: INPUT_METHOD.FLOATING_TB,
			mode: MODE.EDITOR,
			selectionCharCount: attributes.selectionCharCount,
			selectionWordCount: attributes.selectionWordCount,
		},
	});
};

function countSegments(input: string, granularity: 'grapheme' | 'word'): number {
	const seg = new Intl.Segmenter('und', { granularity });
	let n = 0;
	for (const _ of seg.segment(input)) {
		n++;
	}
	return n;
}

function countWordLike(input: string): number {
	const seg = new Intl.Segmenter('und', { granularity: 'word' });
	let n = 0;
	for (const part of seg.segment(input)) {
		if (part.isWordLike) {
			n++;
		}
	}
	return n;
}

const computeCharWordCounts = (
	raw: string,
): {
	charCount: number;
	wordCount: number;
} => {
	// drop all whitespace for char count
	const noWhitespace = raw.replace(/\s+/gu, '');

	// normalize whitespace for word count
	const normalized = raw.replace(/\s+/gu, ' ').trim();

	const hasSegmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function';

	const charCount = hasSegmenter
		? countSegments(noWhitespace, 'grapheme')
		: Array.from(noWhitespace).length;

	const wordCount =
		normalized === ''
			? 0
			: hasSegmenter
				? countWordLike(normalized)
				: // simple language-agnostic fallback
					normalized.split(WHITESPACE_REGEX).length;

	return { charCount, wordCount };
};

export const rangeCharWordCount = (
	doc: PMNode,
	from: number,
	to: number,
): { charCount: number; wordCount: number } => {
	if (from === to) {
		return { charCount: 0, wordCount: 0 };
	}
	if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
		// In-document streaming can leave positions stale relative to the live
		// doc. Reading textBetween with stale positions causes ProseMirror's
		// nodesBetween to crash on `undefined.nodeSize`, which then unmounts
		// the surrounding React subtree (including the AI loading screen) via
		// the error boundary. See getSelectionCategoryUsingState for the same
		// pattern.
		const docSize = doc.content.size;
		const safeFrom = clamp(from, 0, docSize);
		const safeTo = clamp(to, safeFrom, docSize);
		if (safeFrom === safeTo) {
			return { charCount: 0, wordCount: 0 };
		}
		// keep line breaks as newlines and leaf nodes as spaces
		const raw = doc.textBetween(safeFrom, safeTo, '\n', ' ');
		return computeCharWordCounts(raw);
	} else {
		// keep line breaks as newlines and leaf nodes as spaces
		const raw = doc.textBetween(from, to, '\n', ' ');
		return computeCharWordCounts(raw);
	}
};

export const selectionCharWordCount = (
	state: EditorState,
): {
	charCount: number;
	wordCount: number;
} => {
	const { from, to } = state.selection;
	if (state.selection.empty) {
		return { charCount: 0, wordCount: 0 };
	}
	const raw = state.doc.textBetween(from, to, '\n', ' ');
	return computeCharWordCounts(raw);
};
